import { css } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { config } from '@config/pendo.config.json';
import { savePersonalizedTakeawaysSettings } from 'redux/actions/help-options';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { useAppDispatch } from 'redux/store';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvIcon from 'shared/components/nv-icon';
import {
  black,
  gray2,
  gray6,
  gray7,
  primary,
} from 'styles/global_defaults/colors';
import {
  headerLineHeight,
  textLargeBodyFontSize,
} from 'styles/global_defaults/fonts';
import { doubleSpacing, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

type PersonalizedTakeawaysSettingsModalProps = {
  show: boolean;
  onClose: () => void;
  isAiCapabilitiesEnabled: boolean;
  initialEnabled: boolean;
  catalogId: string;
  courseAlias: string;
  onSave: (enabled: boolean) => void;
};

export const modalBodyStyles = css`
  .takeaways-modal-content {
    &.disabled {
      padding: ${standardSpacing}px ${standardSpacing}px ${doubleSpacing}px;
      background: ${gray7};
    }
  }
  .header-row {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .description {
    font-size: ${textLargeBodyFontSize}px;
    line-height: ${headerLineHeight}px;
    margin-bottom: 0;
    padding: ${standardSpacing}px 0;
  }

  .disabled-alert {
    color: ${black};
    padding-top: ${halfSpacing}px;
    text-align: left;
  }

  .switch-row {
    align-items: center;
    color: ${gray2};
    display: flex;
    margin-top: 0;
    .switch-disabled {
      .switch-container, .switch-content-container{
        background: ${gray6};
      }
    }
  }

  .example-link {
    color: ${primary};
    display: inline-block;
    margin-left: 50px;
    margin-top: 4px;
    text-decoration: none;

    &:hover,
    &:focus {
      color: ${primary};
      text-decoration: underline;
    }
  }

  // TODO [NOV-105812]: Delete this when the ""See an Example" is ready to be shown again. For now, we are hiding it until the definition is ready.
  .example-link {
    visibility: hidden;
    display: none;
  }
`;

const modalStyles = css`
  .bs4-modal-body {
    padding: ${doubleSpacing}px;
  }
`;

const buttonsSectionStyles = css`
  display: flex;
  justify-content: center;
  margin-top: 65px;
`;

const PersonalizedTakeawaysSettingsModal = ({
  show,
  onClose,
  isAiCapabilitiesEnabled,
  initialEnabled,
  catalogId,
  courseAlias,
  onSave,
}: PersonalizedTakeawaysSettingsModalProps) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const dispatch = useAppDispatch();
  const [enabledDraft, setEnabledDraft] = useState(initialEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = React.useRef(false);

  React.useEffect(() => {
    if (show) {
      setEnabledDraft(initialEnabled);
    }
  }, [show, initialEnabled]);

  const isDirty = useMemo(
    () => enabledDraft !== initialEnabled,
    [enabledDraft, initialEnabled],
  );

  const handleSave = () => {
    if (!isDirty || isSavingRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);
    dispatch(savePersonalizedTakeawaysSettings({
      catalogId,
      enabled: enabledDraft,
    }))
      .then((resultAction: { error?: unknown }) => {
        if (!resultAction?.error) {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: enabledDraft
              ? t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.ENABLE_SUCCESS({ courseAlias })
              : t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.DISABLE_SUCCESS({ courseAlias }),
          }));
          onSave(enabledDraft);
          onClose();
        }
      })
      .finally(() => {
        isSavingRef.current = false;
        setIsSaving(false);
      });
  };

  const onSeeExampleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onClose();
    $state.go('course-personalized-takeaways', {
      catalogId,
      from: $state.current.name,
    });
  };

  const body = (
    <div css={modalBodyStyles}>
      <div className={`takeaways-modal-content ${isAiCapabilitiesEnabled ? '' : 'disabled'}`}>
        <div className='header-row'>
          {isAiCapabilitiesEnabled
            ? <GradientAiIcon icon='gen-ai' size='small' className='mr-2' />
            : <NvIcon icon='gen-ai' size='small' className='mr-2 text-gray-5' />}
          <span className={`text-large-regular font-weight-bolder ${isAiCapabilitiesEnabled ? '' : ' text-gray-3'}`}>
            {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.HEADER()}
          </span>
        </div>

        {!isAiCapabilitiesEnabled && (
          <div className='disabled-alert text-small semi-bold'>
            {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.AI_DISABLED_ALERT()}
          </div>
        )}

        <div className='description'>
          {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.DESCRIPTION({ courseAlias })}
        </div>

        <div className='switch-row'>
          <NvSwitch
            checked={enabledDraft}
            onChange={setEnabledDraft}
            enabledIcon='gen-ai'
            className={`${!isAiCapabilitiesEnabled ? 'switch-disabled' : ''}`}
            dataQa={config.pendo.aiLearning.togglePersonalizedTakeaways}
            disabled={!isAiCapabilitiesEnabled}
            ariaLabel={t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.SWITCH_LABEL({ courseAlias })}
          />
          <span className={`text-medium ml-2${isAiCapabilitiesEnabled ? '' : ' text-gray-1'}`}>{t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.SWITCH_LABEL({ courseAlias })}</span>
        </div>
        <a
          href='#'
          className='example-link text-medium'
          data-qa={config.pendo.aiLearning.seeExamplePersonalizedTakeaways}
          onClick={onSeeExampleClick}
        >
          {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.SEE_AN_EXAMPLE()}
        </a>

        <div css={buttonsSectionStyles}>
          <Button type='button' className='mr-2' variant='secondary' data-qa={config.pendo.aiLearning.cancelPersonalizedTakeaways} onClick={onClose} disabled={isSaving}>{t.FORM.CANCEL()}</Button>
          <Button type='button' variant='primary' data-qa={config.pendo.aiLearning.savePersonalizedTakeaways} onClick={handleSave} disabled={!isDirty || isSaving}>{isSaving ? t.FORM.SAVING() : t.FORM.SAVE()}</Button>
        </div>
      </div>
    </div>
  );

  return (
    <NvModal
      show={show}
      type={ModalType.FIXED}
      width={600}
      height={600}
      header={t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.TITLE()}
      onClose={onClose}
      body={body}
      modalStyles={modalStyles}
    />
  );
};

export default PersonalizedTakeawaysSettingsModal;
